/**
 * Merchant-facing copy recipes for scheduled-task plan steps.
 * Presentational only — no backend coupling.
 */

import type { LucideIcon } from 'lucide-react'
import {
  friendlyToolTitle,
  planStepPrimaryCopy,
  planStepTechnicalName,
  type PlanToolStep,
} from './taskPlanDisplay'

export type StepRecipe = {
  /** Optional icon; number badge remains primary in the card. */
  icon?: LucideIcon
  verb: string
  subject: (config: Record<string, unknown>, step: PlanToolStep) => string
  detail: (config: Record<string, unknown>, step: PlanToolStep) => string | null
}

/** Sentinel used when no named recipe matches — headline uses title-case / display_description. */
export const FALLBACK_RECIPE: StepRecipe = {
  verb: '',
  subject: () => '',
  detail: () => null,
}

function truthyFlag(config: Record<string, unknown>, ...keys: string[]): boolean {
  for (const key of keys) {
    const v = config[key]
    if (v === true || v === 1 || v === '1' || v === 'true') return true
  }
  return false
}

function capitalizeSentence(s: string): string {
  const t = s.trim()
  if (!t) return t
  return t.charAt(0).toUpperCase() + t.slice(1)
}

function joinFilters(parts: string[]): string | null {
  if (!parts.length) return null
  if (parts.length === 1) return capitalizeSentence(parts[0])
  const last = parts[parts.length - 1]
  return capitalizeSentence(`${parts.slice(0, -1).join(', ')} and ${last}`)
}

function stepConfig(step: PlanToolStep): Record<string, unknown> {
  const params = step?.tool_parameters
  if (!params || typeof params !== 'object' || Array.isArray(params)) return {}
  return params as Record<string, unknown>
}

/** Normalize tool id for registry lookup. */
export function normalizeStepToolName(toolName: string | null | undefined): string {
  return String(toolName || '')
    .replace(/^mcp__payhelm__/i, '')
    .replace(/^mcp__/i, '')
    .trim()
    .toLowerCase()
}

function staticRecipe(verb: string, subject: string, detail: string | null = null): StepRecipe {
  return {
    verb,
    subject: () => subject,
    detail: () => detail,
  }
}

function reportRecipe(label: string): StepRecipe {
  return {
    verb: 'Pulls',
    subject: () => label,
    detail: () => null,
  }
}

const REPORT_LABELS: Record<string, string> = {
  get_brands_report: 'your brands report',
  get_charts_report: 'your charts report',
  get_custom_order_bases_report: 'your custom order bases report',
  get_customers_report: 'your customers report',
  get_line_items_report: 'your line items report',
  get_marketing_report: 'your marketing report',
  get_orders_report: 'your orders report',
  get_product_trends_report: 'your product trends report',
  get_products_report: 'your products report',
  get_skus_report: 'your SKUs report',
  get_trends_report: 'your trends report',
  get_variants_report: 'your variants report',
  get_year_comparison_report: 'your year comparison report',
}

/** Named recipes — add one object per tool; render path never branches on tool name. */
export const STEP_RECIPES: Record<string, StepRecipe> = {
  // Sheets / order metadata
  read_public_google_sheet: {
    verb: 'Reads data from',
    subject: () => 'your Google Sheet',
    detail: () => null,
  },
  import_order_metadata_csv: {
    verb: 'Imports order data',
    subject: () => 'from the sheet above',
    detail: (config) => {
      const filters: string[] = []
      if (truthyFlag(config, 'skipEmptyCostRows', 'skip_empty_cost_rows')) {
        filters.push('skips rows with no cost')
      }
      if (truthyFlag(config, 'filterInvalidOrders', 'filter_invalid_orders')) {
        filters.push('filters out invalid orders')
      }
      if (truthyFlag(config, 'start_backfill', 'startBackfill')) {
        filters.push('starts a metadata backfill')
      }
      return joinFilters(filters)
    },
  },
  update_order_metadata: {
    verb: 'Updates order metadata',
    subject: () => 'with the imported records',
    detail: () => null,
  },
  get_order_metadata: {
    verb: 'Looks up',
    subject: () => 'existing order metadata',
    detail: () => null,
  },

  // Google Ads
  get_google_ads_accounts: staticRecipe('Lists', 'your connected Google Ads accounts'),
  search_google_ads: staticRecipe('Searches', 'your Google Ads data'),
  list_google_ads_client_accounts: staticRecipe('Lists', 'Google Ads client accounts'),
  update_google_ads_campaign: staticRecipe('Updates', 'a Google Ads campaign'),

  // Export
  export_to_csv: staticRecipe('Exports', 'results as a CSV download'),
  export_to_email: staticRecipe('Emails', 'the results to you'),
  export_to_google_drive: staticRecipe('Saves', 'the results to Google Drive'),

  // Platforms
  list_connected_platforms: staticRecipe('Lists', 'your connected store and ad platforms'),

  // Traffic
  get_cross_platform_ads_performance: staticRecipe('Pulls', 'cross-platform ads performance'),
  get_website_traffic_summary: staticRecipe('Summarizes', 'your website traffic'),

  // Wave 2 (cheap)
  makeswift_create_page: staticRecipe('Creates', 'a Makeswift page'),
  makeswift_get_page: staticRecipe('Fetches', 'a Makeswift page'),
  makeswift_get_pages: staticRecipe('Lists', 'your Makeswift pages'),
  makeswift_update_component: staticRecipe('Updates', 'a Makeswift component'),
  list_tag_manager_accounts: staticRecipe('Lists', 'your Tag Manager accounts'),
  list_tag_manager_containers: staticRecipe('Lists', 'your Tag Manager containers'),
  create_tag_manager_tag: staticRecipe('Creates', 'a Tag Manager tag'),
  publish_tag_manager_container_version: staticRecipe('Publishes', 'a Tag Manager container version'),
  shopify_get_orders: staticRecipe('Fetches', 'Shopify orders'),
  shopify_get_products: staticRecipe('Fetches', 'Shopify products'),
  run_google_analytics_report: staticRecipe('Runs', 'a Google Analytics report'),
}

for (const [name, label] of Object.entries(REPORT_LABELS)) {
  STEP_RECIPES[name] = reportRecipe(label)
}

/**
 * Resolve a recipe for a tool name.
 * Returns FALLBACK_RECIPE when unknown (never null) so callers always get a safe recipe.
 * Use `hasNamedStepRecipe` to distinguish named vs fallback.
 */
export function resolveStepRecipe(toolName: string | null | undefined): StepRecipe {
  const key = normalizeStepToolName(toolName)
  if (key && STEP_RECIPES[key]) return STEP_RECIPES[key]
  return FALLBACK_RECIPE
}

export function hasNamedStepRecipe(toolName: string | null | undefined): boolean {
  const key = normalizeStepToolName(toolName)
  return Boolean(key && STEP_RECIPES[key])
}

/**
 * Merchant headline:
 * - named recipe → `${verb} ${subject(config)}`
 * - fallback → display_description || title-cased tool name
 */
export function merchantStepHeadline(step: PlanToolStep, recipe?: StepRecipe): string {
  const r = recipe ?? resolveStepRecipe(step?.tool_name)
  const named = hasNamedStepRecipe(step?.tool_name)
  if (!named || r === FALLBACK_RECIPE) {
    return planStepPrimaryCopy(step) || friendlyToolTitle(step?.tool_name)
  }
  const config = stepConfig(step)
  const subject = String(r.subject(config, step) || '').trim()
  const verb = String(r.verb || '').trim()
  return `${verb} ${subject}`.trim() || planStepPrimaryCopy(step)
}

/**
 * Optional detail line. Null → omit entirely.
 * Named recipes only; fallback never shows a detail line.
 */
export function merchantStepDetail(step: PlanToolStep, recipe?: StepRecipe): string | null {
  const r = recipe ?? resolveStepRecipe(step?.tool_name)
  if (!hasNamedStepRecipe(step?.tool_name) || r === FALLBACK_RECIPE) return null
  const config = stepConfig(step)
  const detail = r.detail(config, step)
  if (detail == null) return null
  const trimmed = String(detail).trim()
  return trimmed || null
}

/** Dev-panel payload for a step (raw fields). */
export function developerStepPayload(step: PlanToolStep): {
  tool_name: string
  tool_parameters: Record<string, unknown>
  output_mapping: Record<string, string> | null
  depends_on: number | null
  execution_order: number | null
} {
  return {
    tool_name: planStepTechnicalName(step),
    tool_parameters: stepConfig(step),
    output_mapping: step?.output_mapping ?? null,
    depends_on: step?.depends_on ?? null,
    execution_order: step?.execution_order ?? null,
  }
}
